<template>
  <div class="owc-table-detail">
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <div v-if="!isAccordion">
      <h2 class="owc-table-detail__headline wb-grid-col-mq1-12">
        {{ headline }}
      </h2>

      <SlotContainer
        refName="slot-tableCellComponent"
        elementDOM="div"
        class="owc-table-detail__slot-container"
      >
        <slot></slot>
      </SlotContainer>
      <div v-if="footnotes" class="wb-grid-col-mq1-12 owc-table-detail__footnotes-container">
        <globalelem-footnote-component :module-id="moduleId" theme="light"></globalelem-footnote-component>
      </div>
    </div>

    <div class="wb-grid-col-mq1-12" v-if="isAccordion">
      <wb-accordion class="owc-table-detail__accordion">
        <wb-accordion-item :open="isEditMode" @click.once="handleAccordionClick">
          <wb-accordion-toggle>{{ headline }}</wb-accordion-toggle>
          <wb-accordion-section class="owc-wb-accordion__section">
            <wb-accordion-mobile-header>{{ backLabel }}</wb-accordion-mobile-header>
            <wb-accordion-content>
              <SlotContainer
                refName="slot-tableCellComponent"
                elementDOM="div"
                class="owc-table-cell"
                :slot-component-data="JSON.stringify({ isInModal: true })"
              >
                <slot></slot
              ></SlotContainer>
              <div
                v-if="shouldShowLocalFootnotes"
                class="wb-grid-col-mq1-12 owc-table-detail__footnotes-container"
              >
                <globalelem-footnote-component
                  :module-id="moduleId"
                  theme="light"
                ></globalelem-footnote-component>
              </div>
            </wb-accordion-content>
          </wb-accordion-section>
        </wb-accordion-item>
      </wb-accordion>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * Table Detail Component
 * The table detail module is going to be use on the PI pages.
 * Inside the module, the author can configure:
 *  - headline (string)
 *  - labelBackMobile (string)
 * The API will add further information:
 *  - insideAccordion (boolean)
 *  - footnotes (Footnote[])
 *
 * The table detail component will render table cell slots inside.
 * It will render either as a standalone table or behave like an accordion,
 * depending on what setting the author has used on the table module.
 *
 * @vue-prop {string} datajson - Config and Component's data coming from CMS
 *
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import { TableDetailData, TableDetailMethods, TableDetailComputed, TableDetailProps } from './TableDetail';
import OwcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import { getLocalFootnotes, setModuleId } from '../../_utils/Footnotes/FootnotesHelper';
import { WB_RESIZE_EVENT_NAME } from '../../_utils/MediaQueryService/MediaQueryUtils';
import { isMobileLayout, updateLayout } from './TableUtils';

const parentIds = new Map();
let currentParentIdIteration = 1;

export default ComponentInitializeDispatcher.extend<
  TableDetailData,
  TableDetailMethods,
  TableDetailComputed,
  TableDetailProps
>({
  name: 'owc-table-detail',
  components: {
    SlotContainer,
    ValidationBoxWrapper,
  },
  mixins: [OwcDataComponent],
  mounted(): void {
    window.addEventListener(
      WB_RESIZE_EVENT_NAME,
      (updateLayout as unknown) as EventListenerOrEventListenerObject,
    );
  },
  beforeDestroy(): void {
    window.removeEventListener(
      WB_RESIZE_EVENT_NAME,
      (updateLayout as unknown) as EventListenerOrEventListenerObject,
    );
  },
  computed: {
    headline: function() {
      return this.componentData?.headline;
    },
    backLabel: function() {
      return this.componentData?.labelBackMobile;
    },
    isAccordion: function() {
      return this.componentData?.insideAccordion;
    },
    accordionIndex() {
      return parentIds.get(this.componentData.parentAccordionId);
    },
    footnotes() {
      const footnotes = getLocalFootnotes(this.componentData);
      return footnotes && footnotes?.length > 0 ? footnotes : undefined;
    },
    shouldShowLocalFootnotes() {
      return !!(this.footnotes || isMobileLayout);
    },
    moduleId() {
      return setModuleId(this);
    },
  },
  methods: {
    handleAccordionClick: function() {
      if (this.accordionIndex) {
        this.handleCustomTracking(
          this.$el,
          `accordion${this.accordionIndex}.${this.headline}`,
          'link',
          'click',
        );
      }
    },
  },
  watch: {
    componentData(data) {
      if (data.parentAccordionId) {
        const currentIdx = parentIds.has(data.parentAccordionId);

        if (this.isAccordion && !currentIdx) {
          parentIds.set(data.parentAccordionId, currentParentIdIteration);
          currentParentIdIteration += 1;
        }
      }
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils/z-index-list';
@import '@owc-styles/utils/rte';

.owc-table-detail {
  &__headline {
    margin-bottom: var(--wb-spacing-xs);

    @include wb-type-heading-s;
    color: var(--wb-grey-20);
  }
  &__slot-container {
    margin-bottom: var(--wb-spacing-m);
  }
  &__footnotes {
    @include wb-type-hint;
  }
  &__footnote {
    display: flex;

    color: var(--wb-grey-40);

    &:not(:last-child) {
      margin-bottom: var(--wb-spacing-3xs);
    }
  }
  &__accordion {
    border-top-color: transparent;

    wb-accordion-content {
      padding: 0;
    }
    .owc-table-cell {
      margin-bottom: var(--wb-spacing-m);
    }
    .owc-table-detail__footnotes-container {
      margin-bottom: var(--wb-spacing-m);

      @include breakpoint-from(mq3) {
        margin-bottom: var(--wb-spacing-s);
      }
    }
  }
  //Forces higher specificity
  .owc-table-detail__accordion {
    .owc-wb-accordion__section {
      @include breakpoint-to(mq5) {
        z-index: $z-index-overlay;
      }
    }
  }
}
</style>
